import * as THREE from 'three';
import { FLAG_BLOCK, FLAG_WATER, type GameMap } from '@sunderfen/shared';
import { G, addMesh, mat, flames, mergeStatic } from './models.js';
import type { Glows } from './glows.js';
import type { Particles } from './particles.js';

/** Something that gives off light: a fire, a lamp, a window, the beacon. */
export interface LightSource { x: number; y: number; z: number; color: number; intensity: number; range: number; flicker: number; night: number; phase: number }

/**
 * A fixed pool of real point lights handed each frame to the light sources nearest the
 * camera, so firelight actually falls on the ground, walls and people around it.
 * The pool size never changes at runtime (that would recompile every shader); quality
 * presets choose it once. One extra light follows the player after dark.
 */
export class LightPool {
  readonly sources: LightSource[] = [];
  private lights: THREE.PointLight[] = [];
  private carry: THREE.PointLight;
  constructor(private scene: THREE.Scene, count: number) {
    for (let i = 0; i < count; i++) { const l = new THREE.PointLight(0xffa050, 0, 10, 1.6); l.castShadow = false; scene.add(l); this.lights.push(l); }
    // the player's own glow, like a carried torch: present on every preset (one light, no shadows)
    this.carry = new THREE.PointLight(0xffc888, 0, 11, 1.3);
    scene.add(this.carry);
  }
  add(s: Omit<LightSource, 'phase'>) { this.sources.push({ ...s, phase: Math.random() * 10 }); }

  update(time: number, tx: number, tz: number, night: number, carry: { x: number; y: number; z: number } | null) {
    this.carryAt(time, night, carry);
    if (!this.lights.length) return;
    const ranked = this.sources
      .map(s => ({ s, d: (s.x - tx) ** 2 + (s.z - tz) ** 2, on: Math.min(1, (1 - s.night) + s.night * night) }))
      .filter(r => r.on > 0.02 && r.d < 40 * 40)
      .sort((a, b) => a.d - b.d);
    this.lights.forEach((l, i) => {
      const r = ranked[i];
      if (!r) { l.intensity = 0; return; }
      const s = r.s, f = s.flicker ? 1 - s.flicker * 0.35 + Math.sin(time * 9.7 + s.phase) * s.flicker * 0.18 + Math.sin(time * 23.1 + s.phase * 2) * s.flicker * 0.17 : 1;
      // fade lights in at the edge of the pool so swapping sources isn't visible
      const edge = Math.min(1, Math.max(0, (40 * 40 - r.d) / (12 * 12)));
      l.position.set(s.x, s.y, s.z); l.color.setHex(s.color); l.distance = s.range;
      l.intensity = s.intensity * r.on * f * edge;
    });
  }
  private carryAt(time: number, night: number, carry: { x: number; y: number; z: number } | null) {
    if (!carry) { this.carry.intensity = 0; return; }
    this.carry.position.set(carry.x + 0.3, carry.y + 2.0, carry.z + 0.2);
    const k = Math.min(1, Math.max(0, (night - 0.15) / 0.5));
    this.carry.intensity = k * k * (3 - 2 * k) * 7 * (0.94 + Math.sin(time * 7.3) * 0.04 + Math.sin(time * 17.9) * 0.02);
  }
}

/** Lamp posts by doors and paths, lit at dusk. */
export function buildLamps(map: GameMap, scene: THREE.Scene, glows: Glows, pool: LightPool, spots: [number, number][]) {
  for (const [x, z] of spots) {
    const g = new THREE.Group(), y = map.heightAt(x + 0.5, z + 0.5);
    g.position.set(x + 0.5, y, z + 0.5);
    addMesh(g, G.cyl(0.05, 0.07, 1.9, 6), 0x3a2e24, 0, 0.95, 0);
    addMesh(g, G.box(0.36, 0.05, 0.05), 0x3a2e24, 0.14, 1.85, 0);
    addMesh(g, G.box(0.18, 0.04, 0.18), 0x2a2420, 0.3, 1.8, 0);
    const lamp = new THREE.Mesh(G.box(0.14, 0.2, 0.14), new THREE.MeshBasicMaterial({ color: 0xffc070 }));
    lamp.position.set(0.3, 1.66, 0); g.add(lamp);
    flames.push(lamp);
    addMesh(g, G.cone(0.13, 0.1, 4), 0x2a2420, 0.3, 1.82, 0).rotation.y = Math.PI / 4;
    mergeStatic(g, m => m === lamp);
    scene.add(g);
    glows.add(x + 0.8, y + 1.66, z + 0.5, 0xffc070, 1.8, { base: 0.05, night: 0.9, flicker: 0.12 });
    pool.add({ x: x + 0.8, y: y + 1.6, z: z + 0.5, color: 0xffb060, intensity: 2.2, range: 7, flicker: 0.15, night: 1 });
  }
}

/**
 * Glowing mushrooms in the fen and the grove: faint by day, bright blue-green at night.
 * One instanced mesh; the night glow is a single uniform.
 */
export class GlowShrooms {
  readonly mat: THREE.MeshBasicMaterial;
  private mesh: THREE.InstancedMesh | null = null;
  readonly spots: [number, number, number][] = [];
  constructor(map: GameMap, scene: THREE.Scene, regions: string[], count: number) {
    const keys = new Set(map.regions.filter(r => regions.includes(r.key)).map(r => r.id));
    const cand: number[] = [];
    for (let t = 0; t < map.width * map.height; t++) if (keys.has(map.region[t]) && !(map.flags[t] & (FLAG_WATER | FLAG_BLOCK)) && map.objAt[t] < 0) cand.push(t);
    this.mat = new THREE.MeshBasicMaterial({ color: 0x5ae8c8 });
    if (!cand.length) return;
    const geo = G.cone(0.08, 0.07, 6); geo.translate(0, 0.12, 0);
    const stem = G.cyl(0.02, 0.025, 0.1, 4); stem.translate(0, 0.05, 0);
    this.mesh = new THREE.InstancedMesh(geo, this.mat, count);
    const stems = new THREE.InstancedMesh(stem, mat(0xd8e8d0), count);
    let s = 1337;
    const rnd = () => { s = (s * 16807) % 2147483647; return s / 2147483647; };
    const m4 = new THREE.Matrix4(), q = new THREE.Quaternion(), v = new THREE.Vector3(), sc = new THREE.Vector3();
    for (let i = 0; i < count; i++) {
      const t = cand[Math.floor(rnd() * cand.length)], x = (t % map.width) + rnd(), z = Math.floor(t / map.width) + rnd();
      const size = 0.6 + rnd() * 0.9;
      m4.compose(v.set(x, map.heightAt(x, z) - 0.01, z), q.setFromAxisAngle(new THREE.Vector3(0, 1, 0), rnd() * 6), sc.set(size, size, size));
      this.mesh.setMatrixAt(i, m4); stems.setMatrixAt(i, m4);
      if (i % 9 === 0) this.spots.push([x, map.heightAt(x, z) + 0.3, z]);
    }
    scene.add(this.mesh, stems);
  }
  update(night: number, time: number) {
    const k = 0.25 + night * (1.6 + Math.sin(time * 0.7) * 0.15);
    this.mat.color.setRGB(0.35 * k, 0.9 * k, 0.78 * k);
  }
}

/** Will-o'-wisps drifting over the fen after dark. */
export function wisps(p: Particles, map: GameMap, tx: number, tz: number, night: number) {
  if (night < 0.5 || Math.random() > 0.35 * night) return;
  const x = tx + (Math.random() - 0.5) * 30, z = tz + (Math.random() - 0.5) * 30;
  if (!map.inBounds(Math.floor(x), Math.floor(z))) return;
  const key = map.regions.find(r => r.id === map.region[map.idx(Math.floor(x), Math.floor(z))])?.key;
  if (key !== 'fen' && key !== 'grove') return;
  p.emit({ x, y: Math.max(0, map.heightAt(x, z)) + 0.6 + Math.random(), z, count: 1, spread: 0.2, vy: 0.08, vSpread: 0.15, colors: key === 'fen' ? [0x8af0d0, 0xc8ffe8] : [0xa8f08a, 0xe8ffc8], size: 0.16, life: 6, wander: 0.7, drag: 0.4, alpha: 0.9 }, true);
}
